import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { effectiveBase } from "../lib/price";
import { track, trackNow } from "../lib/analytics";

const CartContext = createContext(null);
const KEY = "ponesi_cart_v2";
const RKEY = "ponesi_cart_restaurant_v2";
const NKEY = "ponesi_cart_note_v2";
const PKEY = "ponesi_cart_prefs_v1";

function computeUnit(product, optionIds) {
  // Discounts (product discount / Happy Hour / restaurant-wide) apply to the base price only;
  // the backend revalidates and is authoritative at order creation.
  let price = effectiveBase(product);
  const names = [];
  const names_en = [];
  const all = [
    ...(product.modifier_groups || []).flatMap((g) => g.options || []),
    ...(product.extras || []),
  ];
  for (const oid of optionIds) {
    const o = all.find((x) => x.id === oid);
    if (o) { price += o.price || 0; names.push(o.name); names_en.push(o.name_en || o.name); }
  }
  return { unit: price, names, names_en };
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [restaurant, setRestaurant] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RKEY)) || null; } catch { return null; }
  });
  // Order-level customer note (applies to the whole order, kept separate from per-item notes).
  const [orderNote, setOrderNote] = useState(() => {
    try { return localStorage.getItem(NKEY) || ""; } catch { return ""; }
  });

  // Explicit checkout preferences (pickup_type / payment_method). Empty for a fresh cart;
  // filled only by an explicit customer tap or by "Order again" (historical convenience defaults).
  const [prefs, setPrefsState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PKEY)) || {}; } catch { return {}; }
  });
  const setPrefs = (patch) => setPrefsState((p) => ({ ...p, ...patch }));

  useEffect(() => { localStorage.setItem(PKEY, JSON.stringify(prefs)); }, [prefs]);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(RKEY, JSON.stringify(restaurant)); }, [restaurant]);
  useEffect(() => { localStorage.setItem(NKEY, orderNote); }, [orderNote]);

  const _push = (product, optionIds, quantity, note, person = "") => {
    const { unit, names, names_en } = computeUnit(product, optionIds);
    const uid = `${product.id}-${optionIds.slice().sort().join("_")}-${note}-${person}`;
    if (items.length === 0) track("cart_created", { restaurant_id: restaurant?.id, product_id: product.id });
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.uid === uid);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...prev, {
        uid, product_id: product.id, name: product.name, name_en: product.name_en || product.name,
        image: product.image,
        unit_price: effectiveBase(product), unit_computed: unit, option_ids: optionIds,
        option_names: names, option_names_en: names_en, quantity, note, person,
      }];
    });
  };

  // Returns 'ok' | 'conflict'
  const addItem = (product, optionIds = [], quantity = 1, note = "", rest = null, person = "") => {
    if (rest && items.length > 0 && restaurant && restaurant.slug !== rest.slug) {
      return "conflict";
    }
    if (rest) setRestaurant(rest);
    _push(product, optionIds, quantity, note, person);
    return "ok";
  };

  const forceReplace = (product, optionIds, quantity, note, rest, person = "") => {
    setItems([]);
    setRestaurant(rest);
    setOrderNote(""); // dropping the old cart also drops its order-level note
    setPrefsState({});
    // push after state reset
    setTimeout(() => _push(product, optionIds, quantity, note, person), 0);
  };

  // Atomic cart rebuild (quick reorder): replaces items, restaurant and prefs in one shot.
  const replaceAll = (entries, rest, nextPrefs = {}) => {
    const built = [];
    for (const e of entries) {
      const { unit, names, names_en } = computeUnit(e.product, e.ids);
      const uid = `${e.product.id}-${e.ids.slice().sort().join("_")}-${e.note}-${e.person}`;
      const found = built.find((i) => i.uid === uid);
      if (found) { found.quantity += e.qty; continue; }
      built.push({
        uid, product_id: e.product.id, name: e.product.name, name_en: e.product.name_en || e.product.name,
        image: e.product.image, unit_price: effectiveBase(e.product), unit_computed: unit, option_ids: e.ids,
        option_names: names, option_names_en: names_en, quantity: e.qty, note: e.note, person: e.person,
      });
    }
    setItems(built);
    setRestaurant(rest);
    setOrderNote("");
    setPrefsState(nextPrefs);
  };

  const setQty = (uid, qty) =>
    setItems((prev) => {
      const it = prev.find((i) => i.uid === uid);
      if (it && qty > 0) {
        track("quantity_changed", { restaurant_id: restaurant?.id, product_id: it.product_id,
                                    from: it.quantity, to: qty });
      } else if (it) {
        track("remove_from_cart", { restaurant_id: restaurant?.id, product_id: it.product_id,
                                    via: "quantity_zero" });
      }
      return qty <= 0 ? prev.filter((i) => i.uid !== uid) : prev.map((i) => (i.uid === uid ? { ...i, quantity: qty } : i));
    });

  const removeItem = (uid) => setItems((prev) => {
    const it = prev.find((i) => i.uid === uid);
    if (it) track("remove_from_cart", { restaurant_id: restaurant?.id, product_id: it.product_id, via: "remove_button" });
    const next = prev.filter((i) => i.uid !== uid);
    if (next.length === 0) { setRestaurant(null); setOrderNote(""); setPrefsState({}); }
    return next;
  });

  const clear = () => { setItems([]); setRestaurant(null); setOrderNote(""); setPrefsState({}); };

  const total = useMemo(() => items.reduce((s, i) => s + i.unit_computed * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  // Abandonment: the visitor leaves with a non-empty cart and no submitted order.
  useEffect(() => {
    const onHide = () => {
      if (!items.length) return;
      trackNow("cart_abandoned", { restaurant_id: restaurant?.id, items: items.length,
                                   units: count, cart_value: Math.round(total) });
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [items, restaurant, count, total]);

  return (
    <CartContext.Provider value={{ items, restaurant, orderNote, setOrderNote, prefs, setPrefs, addItem, forceReplace, replaceAll, setQty, removeItem, clear, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
